import type { AdminPermission } from "./permissions";

export const ADMIN_ROLES = ["owner", "ops_admin", "support", "viewer"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

/**
 * Role → permission grants. Authoritative copy lives on the backend; this
 * client-side map is for navigation gating only.
 */
export const ROLE_PERMISSIONS: Record<AdminRole, readonly AdminPermission[]> = {
  owner: [
    "users:view",
    "users:lock",
    "membership:view",
    "membership:override",
    "membership:restore",
    "membership:trial_extend",
    "membership:commitment_override",
    "membership:trial_convert",
    "hardware:view",
    "hardware:rebind",
    "hardware:blacklist",
    // Owner-only, mirroring the gateway: no other role tuple grants it.
    "hardware:replacement_price_set",
    // WF-57 lost-in-transit: approve a free replacement / file a carrier claim
    // (backend: shipment:replace, owner/ops_admin only).
    "shipment:replace",
    "fulfillment:inventory_load",
    "reclaim:view",
    "reclaim:update",
    "fraud:view",
    "fraud:override",
    "membership:risk_decision:create",
    "membership:risk_decision:apply",
    "disputes:view",
    "disputes:update",
    "notice:view",
    "billing:simulate",
    "audit:view",
    // WF-56 age-policy incident (owner + ops_admin only; never support).
    "users:age_policy_suspend",
    "support:force_signout",
    "support:password_reset",
    "support:account_delete",
    "support:data_export",
    // WF-47 dimension 5: app_version:view is granted to every role below
    // (support must be able to see the floor); app_version:set stays
    // owner-only, mirroring the gateway: no other role tuple grants it.
    "app_version:view",
    "app_version:set",
  ],
  ops_admin: [
    "users:view",
    "users:lock",
    "membership:view",
    "membership:override",
    "membership:restore",
    "membership:trial_extend",
    "membership:trial_convert",
    "hardware:view",
    "hardware:rebind",
    // WF-57 lost-in-transit: approve a free replacement / file a carrier claim
    // (backend: shipment:replace, owner/ops_admin only).
    "shipment:replace",
    "fulfillment:inventory_load",
    "reclaim:view",
    "reclaim:update",
    "fraud:view",
    "fraud:override",
    "membership:risk_decision:create",
    "membership:risk_decision:apply",
    "disputes:view",
    "disputes:update",
    "notice:view",
    "billing:simulate",
    "audit:view",
    "users:age_policy_suspend",
    "support:force_signout",
    "support:password_reset",
    "support:data_export",
    "app_version:view",
  ],
  support: [
    "users:view",
    "membership:view",
    "hardware:view",
    "reclaim:view",
    "fraud:view",
    // Backend grants support create only; confirming fraud also needs apply.
    "membership:risk_decision:create",
    "disputes:view",
    "notice:view",
    "audit:view",
    "support:force_signout",
    "support:password_reset",
    "app_version:view",
  ],
  viewer: [
    "users:view",
    "membership:view",
    "hardware:view",
    "reclaim:view",
    "fraud:view",
    "disputes:view",
    "notice:view",
    "audit:view",
    "app_version:view",
  ],
};
